import { Injectable } from '@angular/core';

/**
 * Mapeo de rutas a permisos requeridos
 * Cada ruta puede requerir uno o más permisos
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionMappingService {
  /**
   * Mapeo de rutas a permisos requeridos
   * Formato: 'ruta': ['permiso1', 'permiso2'] o 'ruta': 'permiso'
   */
  private routePermissionMap: Record<string, string | string[]> = {
    // Roles
    '/apps/roles': 'Roles.GetAll',

    // Usuarios
    '/apps/users': 'Users.GetAll',

    // Clientes
    '/apps/customers': 'Customers.GetAll',
    '/apps/customer-account': 'Customers.GetById',

    // Vehículos
    '/apps/vehicles': 'Vehicles.GetAll',

    // Plazas/Espacios
    '/apps/plazas': 'Spaces.GetAll',

    // Planes
    '/apps/plans': 'Plans.GetAll',

    // Productos de pase
    '/apps/pass-products': 'Customers.GetAll',

    // Tarifas
    '/apps/daily-rates': 'TariffsDaily.GetAll',
    '/apps/hourly-rates': 'TariffsHourly.GetAll',
    '/apps/night-rates': 'TariffsNight.GetAll',

    // Cajas
    '/apps/CashBoxes': 'CashBox.GetAll',
    '/apps/CashMovements': 'CashMovement.Create',
    '/apps/CashOperationsLogs': 'CashBox.GetAll',

    // Cámaras
    '/apps/Cameras': 'Cameras.GetAll',

    // Detecciones
    '/apps/Detection': 'Cameras.GetAll', // Asumiendo que las detecciones requieren permiso de cámaras

    // Ocupación
    '/apps/ParkingOccupancy': 'ParkingOccupancy.View',

    // Revisar categorías
    '/apps/category-review': 'ParkingOccupancy.View',

    // Pago con Ticket
    '/apps/PayDetection': 'ParkingPayment.Create',

    // Puntos de Venta
    '/apps/sales-points': 'SalesPoints.GetAll',

    // Impuestos
    '/apps/taxes': 'Taxes.GetAll',

    // Categorías de Impuestos
    '/apps/tax-categories': 'TaxCategories.GetAll',

    // Tipo de Gastos
    '/apps/expense-categories': 'ExpenseCategory.GetAll',

    // Plantillas de Notificación
    '/apps/notification-templates': 'NotificationTemplates.GetAll',

    // Libro de ventas
    '/apps/invoices-book': 'Taxes.GetAll', // Asumiendo que requiere permiso de impuestos

    // Cierre de mes
    '/apps/month-close': 'Taxes.GetAll',

    // Métodos de Pago
    '/apps/payment-methods': 'Payments.Create', // Asumiendo que requiere permiso de pagos
  };

  /**
   * Obtiene los permisos requeridos para una ruta
   */
  getPermissionsForRoute(route: string): string[] {
    // Buscar coincidencia exacta
    if (this.routePermissionMap[route]) {
      const permission = this.routePermissionMap[route];
      return Array.isArray(permission) ? permission : [permission];
    }

    // Buscar coincidencia parcial (para rutas con parámetros)
    const matchingRoute = Object.keys(this.routePermissionMap).find(key =>
      route.startsWith(key) || route.includes(key)
    );

    if (matchingRoute) {
      const permission = this.routePermissionMap[matchingRoute];
      return Array.isArray(permission) ? permission : [permission];
    }

    // Si no se encuentra mapeo, retornar array vacío (sin restricción)
    return [];
  }

  /**
   * Verifica si una ruta requiere permisos
   */
  routeRequiresPermission(route: string): boolean {
    return this.getPermissionsForRoute(route).length > 0;
  }
}

